import {
  ArrowRight,
  Check,
  Gift,
  Heart,
  Leaf,
  PackageCheck,
  Play,
  Quote,
  Sparkles
} from "lucide-react";
import { Link } from "react-router";

import {
  categories,
  ingredients,
  products,
  reviews
} from "../../data/home";
import {
  findHomeSection,
  useHomeSections
} from "./HomeSectionCms";

export function CategorySection() {
  const sectionsQuery =
    useHomeSections();

  const cms =
    findHomeSection(
      sectionsQuery.data,
      "SHOP_BY_RITUAL"
    );

  if (
    cms &&
    !cms.isVisible
  ) {
    return null;
  }

  return (
    <section className="rini-section bg-[#fffdf8]">
      <div className="rini-container">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-[#70845b]">
            Explore RiNi
          </p>

          <h2 className="rini-heading mt-4 text-4xl font-semibold sm:text-5xl">
            Care for every everyday ritual
          </h2>

          <p className="mt-5 text-lg leading-8 text-[#746d65]">
            Explore handcrafted skincare, haircare and gifting collections
            thoughtfully created for simple, meaningful self-care.
          </p>
        </div>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {categories.map((category) => (
            <Link
              key={category.name}
              to="/shop"
              className="group overflow-hidden rounded-[2rem] border border-[#5e3b2c]/10 bg-white transition hover:-translate-y-1 hover:shadow-xl"
            >
              <div
                className={`flex aspect-[4/3] items-center justify-center bg-gradient-to-br ${category.tone}`}
              >
                <span className="font-serif text-3xl font-semibold text-white/95">
                  RiNi
                </span>
              </div>

              <div className="p-6">
                <h3 className="font-serif text-2xl text-[#38251e]">
                  {category.name}
                </h3>

                <p className="mt-3 leading-7 text-[#746d65]">
                  {category.description}
                </p>

                <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#5e3b2c]">
                  Explore collection
                  <ArrowRight
                    size={16}
                    className="transition group-hover:translate-x-1"
                  />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export function BestSellerSection() {
  const sectionsQuery =
    useHomeSections();

  const cms =
    findHomeSection(
      sectionsQuery.data,
      "BEST_SELLERS"
    );

  if (
    cms &&
    !cms.isVisible
  ) {
    return null;
  }

  return (
    <section className="rini-section bg-[#f7f1e7]">
      <div className="rini-container">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-[#70845b]">
              {cms?.eyebrow ?? "Customer favourites"}
            </p>

            <h2 className="rini-heading mt-4 text-4xl font-semibold sm:text-5xl">
              RiNi bestsellers
            </h2>
          </div>

          <Link
            to="/shop"
            className="inline-flex items-center gap-2 font-semibold text-[#5e3b2c]"
          >
            View all products
            <ArrowRight size={18} />
          </Link>
        </div>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {products.map((product) => (
            <article
              key={product.name}
              className="overflow-hidden rounded-[2rem] bg-white shadow-sm"
            >
              <div
                className={`relative flex aspect-square items-center justify-center bg-gradient-to-br ${product.tone}`}
              >
                <span className="absolute left-4 top-4 rounded-full bg-white px-3 py-1 text-xs font-semibold text-[#5e3b2c]">
                  {product.badge}
                </span>

                <span className="font-serif text-3xl text-white/90">
                  RiNi
                </span>
              </div>

              <div className="p-5">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#70845b]">
                  {product.category}
                </p>

                <h3 className="mt-2 min-h-14 font-serif text-xl text-[#38251e]">
                  {product.name}
                </h3>

                <div className="mt-4 flex items-center justify-between">
                  <span className="text-lg font-semibold text-[#5e3b2c]">
                    {product.price}
                  </span>

                  <button
                    type="button"
                    className="rounded-full bg-[#5e3b2c] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#38251e]"
                  >
                    Add to cart
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export function WhyRiniSection() {
  const points = [
    {
      icon: Leaf,
      title: "Nature-inspired",
      text: "Formulated with thoughtfully selected botanical ingredients."
    },
    {
      icon: Heart,
      title: "Made with care",
      text: "Every product reflects attention, patience and personal care."
    },
    {
      icon: PackageCheck,
      title: "Small-batch quality",
      text: "Prepared in controlled batches for better attention to detail."
    },
    {
      icon: Sparkles,
      title: "Transparent experience",
      text: "See the ingredients, preparation journey and product story."
    }
  ];

  return (
    <section className="rini-section bg-[#fffdf8]">
      <div className="rini-container">
        <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-[#70845b]">
              Why choose RiNi
            </p>

            <h2 className="rini-heading mt-4 text-4xl font-semibold sm:text-5xl">
              Care that feels honest, personal and beautifully made
            </h2>

            <p className="mt-6 text-lg leading-8 text-[#746d65]">
              RiNi is built around a simple belief: bodycare should be
              thoughtfully created, clearly explained and enjoyable to use.
            </p>

            <Link
              to="/our-story"
              className="rini-button-primary mt-8"
            >
              Discover our story
              <ArrowRight size={18} />
            </Link>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            {points.map((point) => {
              const Icon = point.icon;

              return (
                <article
                  key={point.title}
                  className="rounded-[2rem] border border-[#5e3b2c]/10 bg-[#f7f1e7] p-6"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-[#70845b]">
                    <Icon size={22} />
                  </span>

                  <h3 className="mt-5 font-serif text-2xl text-[#38251e]">
                    {point.title}
                  </h3>

                  <p className="mt-3 leading-7 text-[#746d65]">
                    {point.text}
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

export function MakingSection() {
  const steps = [
    "Ingredients are selected and prepared",
    "Products are handcrafted in controlled batches",
    "Quality, texture and consistency are checked",
    "Every order is carefully packed"
  ];

  return (
    <section className="rini-section overflow-hidden bg-[#3e2d27] text-white">
      <div className="rini-container grid gap-12 lg:grid-cols-2 lg:items-center">
        <div className="relative overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-[#9a826b] to-[#56443a]">
          <div className="flex aspect-video items-center justify-center">
            <button
              type="button"
              className="flex h-20 w-20 items-center justify-center rounded-full bg-white text-[#5e3b2c] shadow-xl transition hover:scale-105"
              aria-label="Play manufacturing video"
            >
              <Play
                size={28}
                fill="currentColor"
              />
            </button>
          </div>

          <div className="absolute bottom-5 left-5 rounded-full bg-black/30 px-4 py-2 text-sm backdrop-blur">
            RiNi making journey
          </div>
        </div>

        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-[#c8d7ba]">
            Made openly, made thoughtfully
          </p>

          <h2 className="mt-4 font-serif text-4xl font-semibold leading-tight sm:text-5xl">
            See how RiNi products come to life
          </h2>

          <p className="mt-6 text-lg leading-8 text-white/75">
            Our website will bring customers closer to the making process
            through preparation videos, ingredient stories, packing moments
            and behind-the-scenes product journeys.
          </p>

          <div className="mt-8 space-y-4">
            {steps.map((step) => (
              <div
                key={step}
                className="flex items-center gap-3"
              >
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#70845b]">
                  <Check size={15} />
                </span>

                <span className="text-white/85">{step}</span>
              </div>
            ))}
          </div>

          <Link
            to="/making-rini"
            className="mt-9 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3.5 font-semibold text-[#38251e]"
          >
            Watch our process
            <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </section>
  );
}

export function IngredientSection() {
  return (
    <section className="rini-section bg-[#edf1e8]">
      <div className="rini-container">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-[#70845b]">
            Ingredient stories
          </p>

          <h2 className="rini-heading mt-4 text-4xl font-semibold sm:text-5xl">
            Inspired by botanicals and traditional care
          </h2>
        </div>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {ingredients.map((ingredient, index) => (
            <article
              key={ingredient.name}
              className="rounded-[2rem] bg-white p-6 text-center"
            >
              <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-[#dfe6d5]">
                <Leaf
                  size={34}
                  className="text-[#70845b]"
                  strokeWidth={1.5}
                />
              </div>

              <p className="mt-4 text-xs font-semibold uppercase tracking-[0.16em] text-[#a27a4d]">
                Botanical {String(index + 1).padStart(2, "0")}
              </p>

              <h3 className="mt-2 font-serif text-2xl text-[#38251e]">
                {ingredient.name}
              </h3>

              <p className="mt-3 leading-7 text-[#746d65]">
                {ingredient.description}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export function GiftSection() {
  return (
    <section className="rini-section bg-[#fffdf8]">
      <div className="rini-container">
        <div className="overflow-hidden rounded-[3rem] bg-[#eee0cf]">
          <div className="grid gap-10 p-8 sm:p-12 lg:grid-cols-[1.1fr_0.9fr] lg:p-16">
            <div>
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white text-[#5e3b2c]">
                <Gift size={25} />
              </span>

              <p className="mt-8 text-sm font-semibold uppercase tracking-[0.22em] text-[#70845b]">
                Gifting with RiNi
              </p>

              <h2 className="rini-heading mt-4 text-4xl font-semibold sm:text-5xl">
                Thoughtful gifts, naturally beautiful
              </h2>

              <p className="mt-6 max-w-xl text-lg leading-8 text-[#746d65]">
                Create meaningful gift hampers for birthdays, weddings,
                festivals, corporate occasions and return gifts.
              </p>

              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  to="/gift-hampers"
                  className="rini-button-primary"
                >
                  Explore hampers
                </Link>

                <a
                  href="https://wa.me/"
                  className="rini-button-secondary"
                >
                  Request custom hamper
                </a>
              </div>
            </div>

            <div className="flex min-h-80 items-center justify-center rounded-[2rem] bg-gradient-to-br from-[#c49a70] to-[#6f5140]">
              <div className="text-center text-white">
                <PackageCheck
                  size={52}
                  className="mx-auto"
                  strokeWidth={1.3}
                />
                <p className="mt-5 font-serif text-3xl">
                  RiNi Gift Collection
                </p>
                <p className="mt-2 text-sm text-white/75">
                  Product image will be added here
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function ReviewSection() {
  return (
    <section className="rini-section bg-[#f7f1e7]">
      <div className="rini-container">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-[#70845b]">
            Customer love
          </p>

          <h2 className="rini-heading mt-4 text-4xl font-semibold sm:text-5xl">
            Kind words from the RiNi community
          </h2>
        </div>

        <div className="mt-12 grid gap-6 lg:grid-cols-3">
          {reviews.map((review) => (
            <article
              key={review.name}
              className="rounded-[2rem] bg-white p-7"
            >
              <Quote
                size={32}
                className="text-[#c59a63]"
                strokeWidth={1.5}
              />

              <p className="mt-6 font-serif text-2xl leading-9 text-[#38251e]">
                “{review.text}”
              </p>

              <div className="mt-7 border-t border-[#5e3b2c]/10 pt-5">
                <p className="font-semibold text-[#5e3b2c]">
                  {review.name}
                </p>

                <p className="mt-1 text-sm text-[#746d65]">
                  RiNi customer
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export function InstagramSection() {
  return (
    <section className="rini-section bg-[#fffdf8]">
      <div className="rini-container text-center">
        <p className="text-sm font-semibold uppercase tracking-[0.22em] text-[#70845b]">
          Follow our handmade journey
        </p>

        <h2 className="rini-heading mt-4 text-4xl font-semibold sm:text-5xl">
          @rini_bodycare
        </h2>

        <div className="mt-10 grid grid-cols-2 gap-3 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div
              key={index}
              className="flex aspect-square items-center justify-center rounded-3xl bg-gradient-to-br from-[#ded2c0] to-[#9f8067]"
            >
              <span className="font-serif text-2xl text-white">
                RiNi
              </span>
            </div>
          ))}
        </div>

        <a
          href="https://www.instagram.com/rini_bodycare/"
          target="_blank"
          rel="noreferrer"
          className="rini-button-secondary mt-9"
        >
          Visit Instagram
          <ArrowRight size={18} />
        </a>
      </div>
    </section>
  );
}
